import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  getCartItemKey,
  computeUnitPrice,
  buildGroupQty,
  buildAddonGroupQty,
  getProductAddonVolumeResolver,
  getVariantConfigFromProduct,
  getLogoAddonsFromProduct,
  getSizeBulkConfigFromProduct
} from '../utils/cartItemHelpers'

export const useCartStore = defineStore('cart', () => {
  const items = ref([])
  const loading = ref(false)

  const totalItems = computed(() => {
    return items.value.reduce((total, item) => total + item.quantity, 0)
  })

  const totalPositions = computed(() => {
    return items.value.length
  })

  const totalAmount = computed(() => {
    const groupQty = buildGroupQty(items.value)
    const addonGroupQty = buildAddonGroupQty(items.value)
    return items.value.reduce((total, item) => {
      const pid = item.product.id
      const variantConfig = getVariantConfigFromProduct(item.product)
      const addonConfigs = getLogoAddonsFromProduct(item.product)
      const map = addonGroupQty[pid] || {}
      const addonResolver = (addon) => map[Number(addon.id)] || 0
      const unit = computeUnitPrice(
        item.product,
        groupQty[pid],
        variantConfig,
        addonConfigs,
        addonResolver,
        getSizeBulkConfigFromProduct(item.product)
      )
      return total + unit * item.quantity
    }, 0)
  })

  const isEmpty = computed(() => items.value.length === 0)

  const getMaxQtyForLine = (product) => {
    if (product.size_id != null && Array.isArray(product.sizes)) {
      const row = product.sizes.find((s) => Number(s.id) === Number(product.size_id))
      if (row) return Number(row.stock_quantity) || 0
    }
    return Number(product.stock_quantity) || 0
  }

  const findCartItem = (product) => {
    const key = getCartItemKey(product)
    return items.value.find((item) => getCartItemKey(item.product) === key)
  }

  const addToCart = async (product, quantity, sizeInfo = null) => {
    try {
      loading.value = true

      const existingItem = findCartItem(product)
      const maxQty = getMaxQtyForLine(product)
      const plannedTotal = existingItem ? existingItem.quantity + quantity : quantity
      if (plannedTotal > maxQty) {
        return false
      }

      if (existingItem) {
        existingItem.quantity += quantity
        if (sizeInfo && !existingItem.size_info) {
          existingItem.size_info = sizeInfo
        }
        if (product.logo_variant_info && !existingItem.logo_variant_info) {
          existingItem.logo_variant_info = product.logo_variant_info
        }
        if (product.logo_variant_image && !existingItem.logo_variant_image) {
          existingItem.logo_variant_image = product.logo_variant_image
        }
      } else {
        items.value.push({
          product,
          quantity,
          size_info: sizeInfo || product.size_info || null,
          logo_variant_info: product.logo_variant_info || null,
          logo_variant_image: product.logo_variant_image || null,
          addedAt: new Date()
        })
      }

      saveToLocalStorage()
      return true
    } catch (error) {
      console.error('Ошибка добавления в корзину:', error)
      return false
    } finally {
      loading.value = false
    }
  }

  const removeFromCart = (productId, sizeId = null, logoVariantId = null) => {
    const index = items.value.findIndex((item) => {
      if (sizeId != null || logoVariantId != null) {
        const matchId = item.product.id === productId
        const matchSize = sizeId == null || item.product.size_id === sizeId
        const matchVariant = logoVariantId == null || item.product.logo_variant_id === logoVariantId
        return matchId && matchSize && matchVariant
      }
      return item.product.id === productId && item.product.size_id == null && item.product.logo_variant_id == null
    })
    if (index !== -1) {
      items.value.splice(index, 1)
      saveToLocalStorage()
    }
  }

  const removeCartItem = (item) => {
    const key = getCartItemKey(item.product)
    const index = items.value.findIndex((row) => getCartItemKey(row.product) === key)
    if (index !== -1) {
      items.value.splice(index, 1)
      saveToLocalStorage()
    }
  }

  const updateQuantity = (productId, quantity, sizeId = null, logoVariantId = null) => {
    const item = items.value.find((row) => {
      if (sizeId != null || logoVariantId != null) {
        return row.product.id === productId
          && (sizeId == null || row.product.size_id === sizeId)
          && (logoVariantId == null || row.product.logo_variant_id === logoVariantId)
      }
      return row.product.id === productId && row.product.size_id == null && row.product.logo_variant_id == null
    })
    if (item) {
      const minQuantity = item.product.min_order_quantity || 1
      const maxQty = getMaxQtyForLine(item.product)
      item.quantity = Math.min(maxQty, Math.max(minQuantity, quantity))
      saveToLocalStorage()
    }
  }

  const clearCart = () => {
    items.value = []
    saveToLocalStorage()
  }

  const getItemQuantity = (productId, sizeId = null, logoVariantId = null) => {
    const item = items.value.find((row) => {
      if (sizeId != null || logoVariantId != null) {
        return row.product.id === productId
          && (sizeId == null || row.product.size_id === sizeId)
          && (logoVariantId == null || row.product.logo_variant_id === logoVariantId)
      }
      return row.product.id === productId && row.product.size_id == null && row.product.logo_variant_id == null
    })
    return item ? item.quantity : 0
  }

  const isInCart = (productId, sizeId = null, logoVariantId = null) => {
    return items.value.some((row) => {
      if (sizeId != null || logoVariantId != null) {
        return row.product.id === productId
          && (sizeId == null || row.product.size_id === sizeId)
          && (logoVariantId == null || row.product.logo_variant_id === logoVariantId)
      }
      return row.product.id === productId && row.product.size_id == null && row.product.logo_variant_id == null
    })
  }

  const saveToLocalStorage = () => {
    try {
      localStorage.setItem('cart', JSON.stringify(items.value))
    } catch (error) {
      console.error('Ошибка сохранения корзины:', error)
    }
  }

  const loadFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem('cart')
      if (saved) {
        items.value = JSON.parse(saved)
      }
    } catch (error) {
      console.error('Ошибка загрузки корзины:', error)
      items.value = []
    }
  }

  loadFromLocalStorage()

  return {
    items,
    loading,
    totalItems,
    totalPositions,
    totalAmount,
    isEmpty,
    addToCart,
    removeFromCart,
    removeCartItem,
    updateQuantity,
    clearCart,
    getItemQuantity,
    isInCart,
    loadFromLocalStorage
  }
})
